import React, {
  Fragment, useCallback, useEffect, useRef, useState,
} from 'react'

import { useLocation } from 'react-router-dom'
import Scroll from 'react-scroll'

import PropTypes from 'prop-types'

import i18n from 'Pods/Shared/I18n'

import { LessThanBreakpoint } from 'Utils/GetBreakpoint'

import EmptyState from './Components/UserEmptyState'
import UserNavBar from './Components/UserNavBar'
import UserTutorials from './Components/UserTutorials'
import UserCoverImage from './UserCoverImage/UserCoverImage'
import UserItemsList from './UserItemsList/UserItemsList'
import UserReviews from './UserReviews/UserReviews'
import UserSidebar from './UserSidebar/UserSidebar'

function pickInitialTab(userTotals) {
  const {
    modelCount,
    ratings: {
      model: { count: modelReviewCount },
      project: { count: projectReviewCount },
    },
    galleryCount,
    tutorialCount,
  } = userTotals

  const hasModels = !!modelCount
  const hasReviews = !!(modelReviewCount + projectReviewCount)

  if (!hasModels && !hasReviews) {
    if (galleryCount) return 'image-gallery'
    if (tutorialCount) return 'tutorials'
  }

  return 'listings'
}

function ProfilePageContent({
  userInfo,
  userTotals,
  visitorActions,
  messagingForbidden = false,
  newRoyaltyUi = false,
  navigation,
}) {
  const location = useLocation()

  const [activeTab, setActiveTab] = useState(() => pickInitialTab(userTotals))
  const [coverImage, setCoverImage] = useState(userInfo.coverImage || '')
  const [isResponsive, setIsResponsive] = useState(false)
  const [cart, setCart] = useState({})

  const userReviewsRef = useRef(null)
  const prevTabRef = useRef(activeTab)

  const scrollToReviews = useCallback(() => {
    const reviewsSection = userReviewsRef.current
    if (!reviewsSection) return

    const reviewsContent = userReviewsRef.current.getBoundingClientRect()
    const navigationHeight = document.getElementById('navigation-header').clientHeight
    const userNavHeight = document.getElementById('user-profile-navigation').clientHeight
    const offsetTop = navigationHeight + userNavHeight

    Scroll.animateScroll.scrollTo(reviewsContent.top + window.pageYOffset - offsetTop)
  }, [])

  const handleCoverImageUpload = (src) => {
    const formData = new FormData()
    formData.append('cover_image', src, 'cover_image.jpg')

    window.axios.put('/api/internal/users/upload-cover-image', formData)
      .then((response) => setCoverImage(response.data.url))
      .catch((error) => {
        const errMsg = error.response.data.errors.join(', ')

        window.App.events.publish('app:notification:flash:error', errMsg
          || i18n.t('failed_to_upload.image', { ns: 'global' }))
      })
  }

  useEffect(() => {
    const onCartUpdate = (legacyCartResponseObject) => {
      setCart({
        value: legacyCartResponseObject.meta.finalPrice,
        creditsAvailable: legacyCartResponseObject.meta.storeCreditsLeft,
        applicableCldValue: legacyCartResponseObject.meta.minPurchaseAmountForCld,
        saleOffDiscount: legacyCartResponseObject.meta.saleOffDiscount,
        isSaleOff: legacyCartResponseObject.meta.saleCampaignActive,
        itemIds: legacyCartResponseObject.data.map((obj) => obj.attributes.itemId),
      })
    }

    const onResize = () => setIsResponsive(LessThanBreakpoint('md'))

    App.events.subscribe('app:cart:updated', onCartUpdate)
    onResize()
    window.addEventListener('resize', onResize)

    if (location.hash === '#reviews') setActiveTab('reviews')
    else if (location.hash === '#tutorials') setActiveTab('tutorials')

    return () => {
      window.removeEventListener('resize', onResize)
      const unsub = App.events.unsubscribe || App.events.remove
      unsub.call(App.events, 'app:cart:updated', onCartUpdate)
    }
  }, [])

  useEffect(() => {
    const prevTab = prevTabRef.current
    prevTabRef.current = activeTab
    if (prevTab === activeTab) return undefined

    if (activeTab === 'reviews') {
      if (prevTab === 'listings') {
        scrollToReviews()
        return undefined
      }
      const t = setTimeout(scrollToReviews, 3000)
      return () => clearTimeout(t)
    }

    Scroll.animateScroll.scrollToTop()
    return undefined
  }, [activeTab, scrollToReviews])

  return (
    <Fragment>
      {isResponsive && (
        <UserCoverImage
          imageSrc={coverImage}
          userName={userInfo.username}
          isResponsive={isResponsive}
        />
      )}
      <div className="container-fixed">
        <div className="row user-profile__row user-profile__sidebar">
          <div className="col-12 col-md-4 col-lg-3 col-xxl-2">
            <UserSidebar
              userActions={visitorActions}
              userInfo={userInfo}
              userRatings={userTotals.ratings}
              messagingForbidden={messagingForbidden}
              newRoyaltyUi={newRoyaltyUi}
              locale={navigation.locale}
            />
          </div>
          <div className="col-12 col-md-8 col-lg-9 col-xxl-10">
            {!isResponsive && (
              <UserCoverImage
                imageSrc={coverImage}
                userName={userInfo.username}
                handleImageUpload={handleCoverImageUpload}
                isResponsive={isResponsive}
              />
            )}
            <UserNavBar
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              counts={userTotals}
              categories={userTotals.itemCategories}
              locale={navigation.locale}
            />
            {activeTab === 'image-gallery' ? (
              userTotals.galleryCount > 0 ? (
                <EmptyState text={i18n.t('profilePage.noArtworksUploaded', { ns: 'rails', lng: navigation.locale })} />
              ) : (
                <EmptyState text={i18n.t('profilePage.noArtworksUploaded', { ns: 'rails', lng: navigation.locale })} />
              )
            ) : (
              activeTab === 'tutorials' ? (
                userTotals.tutorialCount > 0 ? (
                  <UserTutorials userName={userInfo.username} />
                ) : (
                  <EmptyState text={i18n.t('profilePage.noTutorialsUploaded', { ns: 'rails', lng: navigation.locale })} />
                )
              ) : (
                <Fragment>
                  {userTotals.modelCount > 0 ? (
                    <UserItemsList
                      cart={cart}
                      userName={userInfo.username}
                      userTotals={userTotals}
                      locale={navigation.locale}
                    />
                  ) : (
                    <EmptyState text={i18n.t('profilePage.noModelsUploaded', { ns: 'rails', lng: navigation.locale })} />
                  )}
                  <section className="user-reviews" ref={userReviewsRef}>
                    <UserReviews
                      userTotals={userTotals}
                      userRating={userInfo.rating}
                      userName={userInfo.username}
                      locale={navigation.locale}
                    />
                  </section>
                </Fragment>
              )
            )}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

ProfilePageContent.propTypes = {
  userTotals: PropTypes.shape({
    galleryCount: PropTypes.number.isRequired,
  }).isRequired,
  visitorActions: PropTypes.object.isRequired,
  userInfo: PropTypes.shape({
    username: PropTypes.string.isRequired,
    rating: PropTypes.number.isRequired,
    coverImage: PropTypes.string,
  }).isRequired,
  messagingForbidden: PropTypes.bool,
  newRoyaltyUi: PropTypes.bool,
  navigation: PropTypes.shape({
    locale: PropTypes.string,
  }).isRequired,
}

export default ProfilePageContent
